import React, { useEffect, useMemo, useRef } from "react";
import { Stack } from "react-bootstrap";
import { colors } from "src/styles";
import styled from "styled-components";
import * as d3 from "d3";

interface SpendDatum {
  label: string;
  amount: number;
}

interface FinancialSpendTileProps {
  caption: string;
  rows: SpendDatum[];
}

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatSpend = (value: number): string => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}k`;
  return `$${value}`;
};

const topLocationSpend: SpendDatum[] = [
  { label: "Toronto HQ", amount: 186400 },
  { label: "Calgary Office", amount: 141250 },
  { label: "Vancouver Hub", amount: 120700 },
  { label: "Montreal Depot", amount: 97800 },
  { label: "Ottawa Branch", amount: 82440 },
];

const topServiceSpend: SpendDatum[] = [
  { label: "Janitorial", amount: 168100 },
  { label: "HVAC", amount: 153240 },
  { label: "Electrical", amount: 127550 },
  { label: "Plumbing", amount: 102380 },
  { label: "Landscaping", amount: 88210 },
];

const capitalSpend: SpendDatum[] = [
  { label: "Toronto HQ", amount: 240000 },
  { label: "Calgary Office", amount: 198500 },
  { label: "Vancouver Hub", amount: 173200 },
  { label: "Montreal Depot", amount: 146900 },
  { label: "Ottawa Branch", amount: 128400 },
];

const FinancialSpendTile = ({ caption, rows }: FinancialSpendTileProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const data = useMemo(
    () =>
      rows.map((item) => ({ label: item.label, amount: toNumber(item.amount) })).sort((a, b) => b.amount - a.amount),
    [rows],
  );

  useEffect(() => {
    if (!svgRef.current || data.length === 0) return;

    const margin = { top: 12, right: 24, bottom: 28, left: 5 };
    const containerWidth = containerRef.current?.clientWidth || 300;
    const width = containerWidth - margin.left - margin.right;

    const rowHeight = 44;
    const height = data.length * rowHeight;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const maxAmount = d3.max(data, (d) => d.amount) || 0;
    const x = d3
      .scaleLinear()
      .domain([0, maxAmount + Math.ceil(maxAmount * 0.15)])
      .range([0, width]);

    const y = d3
      .scaleBand()
      .domain(data.map((d) => d.label))
      .range([0, height])
      .paddingInner(0.2);

    svg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(
        d3
          .axisBottom(x)
          .ticks(4)
          .tickFormat((v) => formatSpend(Number(v))),
      )
      .selectAll("text")
      .style("font-size", "10px")
      .style("fill", "#888780");
    svg.select(".domain").style("display", "none");

    svg
      .append("g")
      .attr("class", "grid")
      .call(
        d3
          .axisBottom(x)
          .ticks(4)
          .tickSize(height)
          .tickFormat(() => ""),
      )
      .attr("transform", "translate(0,0)")
      .selectAll("line")
      .style("stroke", "rgba(136,135,128,0.15)")
      .style("stroke-dasharray", "3,3");
    svg.select(".grid .domain").remove();

    svg
      .selectAll(".label")
      .data(data)
      .enter()
      .append("text")
      .attr("class", "label")
      .attr("x", 0)
      .attr("y", (d) => (y(d.label) || 0) + 14)
      .text((d) => d.label)
      .style("font-size", "11px")
      .style("font-weight", "500")
      .style("fill", colors.grey.dark);

    svg
      .selectAll(".bar")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", 0)
      .attr("y", (d) => (y(d.label) || 0) + 22)
      .attr("width", 0)
      .attr("height", 8)
      .attr("fill", colors.kleenway.green)
      .attr("rx", 4)
      .style("opacity", 0.9)
      .on("mouseover", function (this: SVGRectElement, _event, d) {
        d3.select(this).style("opacity", 1);

        const tooltip = svg
          .append("g")
          .attr("class", "tooltip")
          .attr("transform", `translate(${x(d.amount) + 6},${(y(d.label) || 0) + 26})`);

        tooltip
          .append("rect")
          .attr("x", 0)
          .attr("y", -11)
          .attr("width", 72)
          .attr("height", 18)
          .attr("fill", "rgba(0,0,0,0.7)")
          .attr("rx", 3);

        tooltip
          .append("text")
          .attr("x", 36)
          .attr("text-anchor", "middle")
          .attr("y", 3)
          .attr("fill", "#ffffff")
          .attr("font-size", "11px")
          .text(formatSpend(d.amount));
      })
      .on("mouseout", function (this: SVGRectElement) {
        d3.select(this).style("opacity", 0.9);
        svg.selectAll(".tooltip").remove();
      })
      .transition()
      .duration(700)
      .delay((_, i) => i * 40)
      .ease(d3.easeQuadOut)
      .attr("width", (d) => x(d.amount));
  }, [data]);

  return (
    <WidgetContainer>
      <Stack gap={2} style={{ minWidth: 0, width: "100%" }}>
        <CardTitle>{caption}</CardTitle>
        <ChartContainer ref={containerRef}>
          {data.length === 0 && <StateText>No data available</StateText>}
          {data.length > 0 && <svg ref={svgRef} style={{ width: "100%", display: "block" }} />}
        </ChartContainer>
        <Footer>spend amount</Footer>
      </Stack>
    </WidgetContainer>
  );
};

const FinancialPanel = () => {
  return (
    <Tiles>
      <FinancialSpendTile caption="Top location spend" rows={topLocationSpend} />
      <FinancialSpendTile caption="Top service spend" rows={topServiceSpend} />
      <FinancialSpendTile caption="Capital spend" rows={capitalSpend} />
    </Tiles>
  );
};

export default FinancialPanel;

const Tiles = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  align-items: stretch;

  @media (max-width: 1280px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 860px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const WidgetContainer = styled.div`
  display: flex;
  min-width: 0;
  background: #ffffff;
  border: 0.5px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 16px;
`;

const ChartContainer = styled.div`
  display: block;
  width: 100%;
  max-height: 240px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.15);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.3);
  }
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.grey.dark};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const StateText = styled.span`
  font-size: 13px;
  color: ${colors.grey.default};
`;

const Footer = styled.div`
  margin-top: 2px;
  font-size: 11px;
  color: ${colors.grey.light10};
  text-transform: lowercase;
`;
